import React, { useEffect, useRef, useState } from "react";
import { gsap } from "../../lib/gsap";
import profile from "../../data/profile";

const BIO = profile.bio;

export default function AboutSection() {
  const sectionRef = useRef(null);
  const contentRef = useRef(null);
  const photoRef = useRef(null);
  const intervalRef = useRef(null);
  const [typed, setTyped] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const section = sectionRef.current;
    const scroller = document.querySelector(".main-scroller");
    if (!section || !scroller) return;

    let active = false;

    function reset() {
      clearInterval(intervalRef.current);
      gsap.set(photoRef.current, { opacity: 0, x: -40 });
      gsap.set(contentRef.current, { opacity: 0, y: 30 });
      setTyped(0); setDone(false);
    }
    function play() {
      reset();
      gsap.to(photoRef.current, { opacity: 1, x: 0, duration: 0.9, ease: "power3.out" });
      gsap.to(contentRef.current, { opacity: 1, y: 0, duration: 0.8, ease: "power3.out", delay: 0.15 });
      let i = 0;
      intervalRef.current = setInterval(() => {
        i = Math.min(i + 5, BIO.length);
        setTyped(i);
        if (i >= BIO.length) { clearInterval(intervalRef.current); setDone(true); }
      }, 18);
    }
    reset();
    function onScroll() {
      const inRange = Math.abs(scroller.scrollTop - section.offsetTop) < window.innerHeight * 0.5;
      if (inRange && !active) { active = true; play(); }
      if (!inRange && active) { active = false; reset(); }
    }
    scroller.addEventListener("scroll", onScroll, { passive: true });
    return () => { clearInterval(intervalRef.current); scroller.removeEventListener("scroll", onScroll); };
  }, []);

  return (
    <section ref={sectionRef} className="snap-section" data-testid="about-section" style={{ background: "var(--ink)", color: "var(--paper)" }}>
      {/* Marquee band top - offset below navbar */}
      <div className="absolute top-16 md:top-20 left-0 right-0 h-12 md:h-14 border-y border-[rgba(245,242,236,0.12)] overflow-hidden flex items-center bg-[var(--ink)] z-[5]">
        <div className="marquee-track flex whitespace-nowrap font-mono text-xs tracking-[0.25em] uppercase text-[color:rgba(245,242,236,0.65)]">
          {[...profile.skills, ...profile.skills].map((s, i) => (
            <span key={i} className="mx-6 flex items-center gap-6">
              {s} <span className="text-[var(--accent)]">✦</span>
            </span>
          ))}
        </div>
      </div>

      <div className="relative h-full w-full grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-16 px-6 md:px-16 pt-28 md:pt-36 md:[@media(max-height:820px)]:pt-24 pb-6 md:pb-10 md:[@media(max-height:820px)]:pb-6">
        {/* Left: giant name / signature panel */}
        <div ref={photoRef} className="md:col-span-4 flex flex-col justify-end">
          <p className="font-mono text-[9px] md:text-[10px] tracking-[0.35em] uppercase text-[var(--accent)] mb-1.5 md:mb-4">About</p>
          <h2 className="font-serif leading-[0.85] tracking-tight text-[clamp(1.75rem,min(9vw,12.5vh),6rem)]">
            The<br />analyst<br /><span className="italic text-[var(--accent)]">behind.</span>
          </h2>
          <div className="mt-3 md:mt-8 md:[@media(max-height:820px)]:mt-5 space-y-1.5 md:space-y-3 text-[13px] md:text-sm text-[color:rgba(245,242,236,0.65)]">
            <p className="flex justify-between border-b border-[rgba(245,242,236,0.1)] pb-1.5 md:pb-2"><span className="font-mono text-[9px] md:text-[10px] tracking-[0.25em] uppercase text-[color:rgba(245,242,236,0.4)]">Location</span> <span>United Kingdom</span></p>
            <p className="flex justify-between border-b border-[rgba(245,242,236,0.1)] pb-1.5 md:pb-2"><span className="font-mono text-[9px] md:text-[10px] tracking-[0.25em] uppercase text-[color:rgba(245,242,236,0.4)]">Focus</span> <span>Business · Strategy</span></p>
            <p className="flex justify-between border-b border-[rgba(245,242,236,0.1)] pb-1.5 md:pb-2"><span className="font-mono text-[9px] md:text-[10px] tracking-[0.25em] uppercase text-[color:rgba(245,242,236,0.4)]">Degree</span> <span>MSc · Manchester</span></p>
          </div>
        </div>

        {/* Right: typewriter bio */}
        <div ref={contentRef} className="md:col-span-8 flex flex-col justify-center">
          <p className="font-serif text-[15px] md:text-4xl md:[@media(max-height:820px)]:text-3xl leading-snug md:leading-relaxed md:[@media(max-height:820px)]:leading-normal">
            {BIO.split("").map((c, i) => (
              <span key={i} style={{
                color: i < typed ? "var(--paper)" : "rgba(245,242,236,0.15)",
                transition: "color 0.15s",
              }}>{c}</span>
            ))}
            {!done && <span className="caret" style={{ color: "var(--accent)" }}>▍</span>}
          </p>

          <div className="mt-3 md:mt-10 md:[@media(max-height:820px)]:mt-6 grid grid-cols-3 gap-2 md:gap-4">
            {profile.stats.map(s => (
              <div key={s.label} className="rounded-xl p-2 md:p-4 md:[@media(max-height:820px)]:p-3 border border-[rgba(245,242,236,0.12)] bg-white/[0.02]">
                <div className="font-serif text-lg md:text-3xl md:[@media(max-height:820px)]:text-2xl">{s.value}</div>
                <div className="font-mono text-[7px] md:text-[10px] tracking-[0.15em] md:tracking-[0.2em] uppercase text-[color:rgba(245,242,236,0.55)] mt-0.5 md:mt-1 whitespace-pre-line">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
